"use client";

import { useState } from "react";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { ECON_CHART_STRINGS } from "@/components/tools/econ-micro/chart-strings";
import { EconChart } from "@/components/tools/econ-micro/curve-chart";
import { ElasticityWorking } from "@/components/tools/econ-micro/elasticity-working";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { arcElasticity } from "@/lib/tool-kit/econ-micro/elasticity";
import { lineThrough } from "@/lib/tool-kit/econ-micro/geometry";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const PRICE_ELASTICITY_CALCULATOR_STRINGS = {
  ...ECON_CHART_STRINGS,
  measureLabel: "Elasticity to calculate",
  measurePrice: "Price elasticity of demand",
  measureIncome: "Income elasticity of demand",
  measureCross: "Cross-price elasticity of demand",
  inputsHeading: "Two observations",
  inputsHint:
    "Enter the value before and after the change, with the quantity demanded each time. The midpoint formula gives the same answer whichever you call “before”.",
  crossHint:
    "Q is the quantity demanded of the good you are studying; P′ is the price of the other good.",
  priceBefore: "Price before (P₁)",
  priceAfter: "Price after (P₂)",
  incomeBefore: "Income before (I₁)",
  incomeAfter: "Income after (I₂)",
  crossBefore: "Other good’s price before (P′₁)",
  crossAfter: "Other good’s price after (P′₂)",
  quantityBefore: "Quantity demanded before (Q₁)",
  quantityAfter: "Quantity demanded after (Q₂)",
  results: "Results",
  invalidInput: "Enter all four values as numbers of 0 or more.",
  sameDriver:
    "The {driver} did not change, so its percentage change is zero and the elasticity is undefined. Enter two different values.",
  emptyQuantity:
    "Both quantities are zero, so the percentage change in quantity cannot be found. Enter a quantity above zero.",
  emptyDriver:
    "Both {driver} values are zero, so the percentage change cannot be found. Enter a value above zero.",
  driverPrice: "price",
  driverIncome: "income",
  driverCross: "other good’s price",
  chartHeading: "Graph",
  priceAxis: "Price (P)",
  incomeAxis: "Income (I)",
  crossAxis: "Price of the other good (P′)",
  quantityAxis: "Quantity demanded (Q)",
  curvePrice: "D",
  curveIncome: "Engel curve",
  curveCross: "",
  pointBefore: "Before",
  pointAfter: "After",
  chartTitlePrice: "Demand line through the two observations",
  chartTitleIncome: "Engel curve through the two observations",
  chartTitleCross: "Quantity demanded against the other good’s price",
  chartDescription:
    "A graph with {yTitle} on the vertical axis and {xTitle} on the horizontal axis. A straight line passes through the point Before ({x1}, {y1}) and the point After ({x2}, {y2}).",
  dataTableDriver: "Observation",
  elasticityPrice: "Price elasticity of demand (E)",
  elasticityIncome: "Income elasticity of demand (E)",
  elasticityCross: "Cross-price elasticity of demand (E)",
  absoluteValue: "Absolute value |E|",
  percentChangeQ: "% change in quantity demanded",
  percentChangePrice: "% change in price",
  percentChangeIncome: "% change in income",
  percentChangeCross: "% change in the other good’s price",
  verdictLabel: "Verdict",
  verdictPerfectlyInelastic: "Perfectly inelastic",
  verdictInelastic: "Inelastic (|E| < 1)",
  verdictUnitElastic: "Unit elastic (|E| = 1)",
  verdictElastic: "Elastic (|E| > 1)",
  verdictInferior: "Inferior good",
  verdictNecessity: "Normal good, necessity",
  verdictLuxury: "Normal good, luxury",
  verdictUnitIncome: "Normal good, unit income elastic",
  verdictSubstitutes: "Substitutes",
  verdictComplements: "Complements",
  verdictUnrelated: "Unrelated goods",
  explainPerfectlyInelastic:
    "Quantity demanded did not respond to the price change at all (E = 0), so the demand line is vertical over this range.",
  explainInelastic:
    "Buyers respond less than proportionally: a 1% price change moves quantity demanded by only about {abs}%.",
  explainUnitElastic:
    "The percentage change in quantity exactly matches the percentage change in price, so total revenue does not change.",
  explainElastic:
    "Buyers respond more than proportionally: a 1% price change moves quantity demanded by about {abs}%.",
  explainInferior:
    "Quantity demanded falls as income rises (E < 0), which is the definition of an inferior good.",
  explainNecessity:
    "Quantity demanded rises with income, but by a smaller percentage (0 ≤ E < 1): a normal good that is a necessity.",
  explainLuxury:
    "Quantity demanded rises by a larger percentage than income (E > 1): a normal good that is a luxury.",
  explainUnitIncome:
    "Quantity demanded rises in exact proportion to income (E = 1), the dividing line between a necessity and a luxury.",
  explainSubstitutes:
    "Quantity demanded of this good moves in the same direction as the other good’s price (E > 0), the sign of substitutes.",
  explainComplements:
    "Quantity demanded of this good moves opposite to the other good’s price (E < 0), the sign of complements.",
  explainUnrelated:
    "Quantity demanded did not respond at all (E = 0), so the goods look unrelated over this range.",
  positiveNote:
    "Quantity moved in the same direction as price here, so these points do not trace a downward-sloping demand curve — more likely a shift in demand, a Giffen or Veblen good, or a data-entry slip. The verdict still reads |E|.",
  workingHeading: "Working (midpoint method)",
  workQ:
    "%ΔQ = (Q₂ − Q₁) ÷ ((Q₁ + Q₂) ÷ 2) = ({q2} − {q1}) ÷ (({q1} + {q2}) ÷ 2) = {dq} ÷ {mq} = {pq}",
  workX:
    "%Δ{sym} = ({sym}₂ − {sym}₁) ÷ (({sym}₁ + {sym}₂) ÷ 2) = ({x2} − {x1}) ÷ (({x1} + {x2}) ÷ 2) = {dx} ÷ {mx} = {px}",
  workE: "E = %ΔQ ÷ %Δ{sym} = {pq} ÷ {px} = {e}",
  roundedNote:
    "Percentages are rounded for display; the elasticity is computed from the unrounded values.",
  revenueHeading: "Total revenue test",
  revenueMeasure: "Total revenue (P × Q)",
  revenueBefore: "Before",
  revenueAfter: "After",
  revenueChange: "Change",
  revenuePriceUpUp:
    "Price rose and total revenue rose, which is what inelastic demand does: quantity fell by a smaller percentage than price rose.",
  revenuePriceUpDown:
    "Price rose and total revenue fell, which is what elastic demand does: quantity fell by a larger percentage than price rose.",
  revenuePriceUpSame:
    "Price rose and total revenue did not change, which is what unit elastic demand does.",
  revenuePriceDownUp:
    "Price fell and total revenue rose, which is what elastic demand does: quantity rose by a larger percentage than price fell.",
  revenuePriceDownDown:
    "Price fell and total revenue fell, which is what inelastic demand does: quantity rose by a smaller percentage than price fell.",
  revenuePriceDownSame:
    "Price fell and total revenue did not change, which is what unit elastic demand does.",
  pointHeading: "Point elasticity along the straight line",
  pointIntro:
    "If demand is the straight line through your two points, its slope is ΔQ ÷ ΔP = {slope}, and E = slope × P ÷ Q changes from point to point:",
  pointStart: "At the starting point: {slope} × ({p} ÷ {q}) = {e}",
  pointMid: "At the midpoint: {slope} × ({p} ÷ {q}) = {e}",
  pointEnd: "At the end point: {slope} × ({p} ÷ {q}) = {e}",
  pointUndefined: "undefined (quantity is zero)",
  pointNote:
    "The midpoint answer is exactly the point elasticity at the middle of the line, so it always falls between the other two.",
} as const;

type Strings = typeof PRICE_ELASTICITY_CALCULATOR_STRINGS;
type Measure = "price" | "income" | "cross";

interface Observation {
  x1: string;
  x2: string;
  q1: string;
  q2: string;
}

const START: Record<Measure, Observation> = {
  price: { x1: "10", x2: "12", q1: "100", q2: "80" },
  income: { x1: "30000", x2: "33000", q1: "50", q2: "56" },
  cross: { x1: "2", x2: "2.5", q1: "100", q2: "112" },
};

const SYMBOL: Record<Measure, string> = {
  price: "P",
  income: "I",
  cross: "P′",
};

function readObservation(raw: Observation) {
  const parsed = [raw.x1, raw.x2, raw.q1, raw.q2].map(parseNumberInput);
  if (parsed.some((v) => v === null || (v ?? 0) < 0)) {
    return null;
  }
  const [x1, x2, q1, q2] = parsed as number[];
  return { x1, x2, q1, q2 };
}

export default function PriceElasticityCalculatorWidget() {
  const strings = useToolUi(PRICE_ELASTICITY_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [measure, setMeasure] = useState<Measure>("price");
  const [values, setValues] = useState(START);
  const raw = values[measure];
  const set = (key: keyof Observation) => (value: string) =>
    setValues((all) => ({
      ...all,
      [measure]: { ...all[measure], [key]: value },
    }));

  const labels = {
    price: [strings.priceBefore, strings.priceAfter],
    income: [strings.incomeBefore, strings.incomeAfter],
    cross: [strings.crossBefore, strings.crossAfter],
  }[measure];
  const driver = {
    price: strings.driverPrice,
    income: strings.driverIncome,
    cross: strings.driverCross,
  }[measure];

  const data = readObservation(raw);
  const arc = data ? arcElasticity(data.q1, data.q2, data.x1, data.x2) : null;
  let problem: string | null = null;
  if (!data) {
    problem = strings.invalidInput;
  } else if (arc === "same-driver") {
    problem = t(strings.sameDriver, { driver });
  } else if (arc === "empty-quantity") {
    problem = strings.emptyQuantity;
  } else if (arc === "empty-driver") {
    problem = t(strings.emptyDriver, { driver });
  }
  const result = data && arc && typeof arc !== "string" ? arc : null;

  return (
    <div className="space-y-6">
      <SelectField
        id="pe-measure"
        label={strings.measureLabel}
        onChange={setMeasure}
        options={[
          { value: "price", label: strings.measurePrice },
          { value: "income", label: strings.measureIncome },
          { value: "cross", label: strings.measureCross },
        ]}
        value={measure}
      />
      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <p className="text-muted-foreground text-sm">
          {strings.inputsHint}
          {measure === "cross" ? ` ${strings.crossHint}` : ""}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="pe-x1"
            label={labels[0]}
            numeric
            onChange={set("x1")}
            value={raw.x1}
          />
          <TextField
            id="pe-x2"
            label={labels[1]}
            numeric
            onChange={set("x2")}
            value={raw.x2}
          />
          <TextField
            id="pe-q1"
            label={strings.quantityBefore}
            numeric
            onChange={set("q1")}
            value={raw.q1}
          />
          <TextField
            id="pe-q2"
            label={strings.quantityAfter}
            numeric
            onChange={set("q2")}
            value={raw.q2}
          />
        </div>
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {result && data ? (
          <>
            <ElasticityWorking
              data={data}
              fmt={fmt}
              measure={measure}
              result={result}
              strings={strings}
              symbol={SYMBOL[measure]}
            />
            <PriceChart
              data={data}
              fmt={fmt}
              measure={measure}
              strings={strings}
            />
          </>
        ) : null}
      </section>
    </div>
  );
}

function PriceChart({
  data,
  measure,
  strings,
  fmt,
}: {
  data: { x1: number; x2: number; q1: number; q2: number };
  measure: Measure;
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}) {
  const yTitle = {
    price: strings.priceAxis,
    income: strings.incomeAxis,
    cross: strings.crossAxis,
  }[measure];
  const title = {
    price: strings.chartTitlePrice,
    income: strings.chartTitleIncome,
    cross: strings.chartTitleCross,
  }[measure];
  const curveLabel = {
    price: strings.curvePrice,
    income: strings.curveIncome,
    cross: strings.curveCross,
  }[measure];
  const xMax = Math.max(data.q1, data.q2) * 1.3;
  const yMax = Math.max(data.x1, data.x2) * 1.3;
  const ends = lineThrough(
    [data.q1, data.x1],
    [data.q2, data.x2],
    10 * Math.max(xMax, yMax)
  );
  const text = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  return (
    <ChartFigure
      apa={false}
      data={toTsv([
        [strings.dataTableDriver, yTitle, strings.quantityAxis],
        [strings.pointBefore, data.x1, data.q1],
        [strings.pointAfter, data.x2, data.q2],
      ])}
      figureNumber="1"
      filename={chartFilename(title, "elasticity-graph")}
      labels={strings}
      note=""
      table={{
        head: [strings.dataTableDriver, yTitle, strings.quantityAxis],
        rows: [
          [strings.pointBefore, text(data.x1), text(data.q1)],
          [strings.pointAfter, text(data.x2), text(data.q2)],
        ],
      }}
      title={title}
    >
      <EconChart
        curves={[
          {
            id: "line",
            points: ends,
            color: CHART_COLORS[0],
            label: curveLabel || undefined,
          },
        ]}
        description={t(strings.chartDescription, {
          yTitle,
          xTitle: strings.quantityAxis,
          x1: text(data.q1),
          y1: text(data.x1),
          x2: text(data.q2),
          y2: text(data.x2),
        })}
        dots={[
          {
            id: "before",
            x: data.q1,
            y: data.x1,
            label: strings.pointBefore,
            color: CHART_COLORS[1],
            position: "ne",
            guides: true,
          },
          {
            id: "after",
            x: data.q2,
            y: data.x2,
            label: strings.pointAfter,
            color: CHART_COLORS[3],
            position: "sw",
            guides: true,
          },
        ]}
        fmt={fmt}
        id="pe-chart"
        label={title}
        xMax={xMax}
        xTitle={strings.quantityAxis}
        yMax={yMax}
        yTitle={yTitle}
      />
    </ChartFigure>
  );
}
